import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  getParentChildren,
  getChildProducts,
  getChildPaymentHistory,
  type ParentChild,
  type AthleteProduct,
  type PaymentHistoryItem,
} from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameHero, GameCard } from "../../../components/game";
import { isIndividualChild } from "../../../constants/individualClub";
import { useAuth } from "../../../context/AuthContext";
import { formatDate } from "../../../lib/dateFormat";
import PayDueCard from "../../../components/app/PayDueCard";

export default function AppParentPayments() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { activeChildId, setActiveChildId, setActiveChildIndividual } = useAuth();

  const [children, setChildren] = useState<ParentChild[]>([]);
  const [selectedChild, setSelectedChild] = useState<ParentChild | null>(null);
  const [products, setProducts] = useState<AthleteProduct[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [clubPaymentMethods, setClubPaymentMethods] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"pending" | "history">("pending");

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadPaymentData();
    }
    setActiveChildIndividual(isIndividualChild(selectedChild));
  }, [selectedChild]);

  const loadChildren = async () => {
    setLoading(true);
    try {
      const data = await getParentChildren();
      setChildren(data);
      if (data.length > 0) {
        const urlChild = searchParams.get("child");
        const resolved =
          (urlChild && data.find(c => c.athlete_id === urlChild)) ||
          (activeChildId && data.find(c => c.athlete_id === activeChildId)) ||
          data[0];
        setSelectedChild(resolved);
        setActiveChildId(resolved.athlete_id);
      }
    } catch (err) {
      console.error("Failed to load children:", err);
    }
    setLoading(false);
  };

  const loadPaymentData = async () => {
    if (!selectedChild) return;
    setLoading(true);
    try {
      const [productsData, historyData] = await Promise.all([
        getChildProducts(selectedChild.athlete_id),
        getChildPaymentHistory(selectedChild.athlete_id),
      ]);
      setProducts(productsData.products);
      setClubPaymentMethods(productsData.club?.payment_methods || []);
      setPaymentHistory(historyData);
    } catch (err) {
      console.error("Failed to load payment data:", err);
    }
    setLoading(false);
  };

  const formatCurrency = (cents: number, currency = "RON") => {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  const pendingProducts = products.filter(p => p.payment_status !== "paid");
  const totalPending = pendingProducts.reduce((sum, p) => sum + (p.product?.price_cents || 0), 0);

  if (loading && children.length === 0) {
    return (
      <MobileAppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </MobileAppLayout>
    );
  }

  const childName = selectedChild?.athlete?.full_name || t("parent.child", "Copilul dvs.");

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header — shared dark hero (no back arrow: reachable from the menu). */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <h1 className="text-xl font-extrabold truncate">{t("payments.title", "Plăți")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5 truncate">{childName}</p>
        </GameHero>

      <div className="px-4 pt-5 space-y-4">
        {/* Child Selector */}
        {children.length > 1 && (
          <select
            value={selectedChild?.athlete_id || ""}
            onChange={e => {
              const child = children.find(c => c.athlete_id === e.target.value);
              setSelectedChild(child || null);
              if (child) setActiveChildId(child.athlete_id);
            }}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {children.map(c => (
              <option key={c.athlete_id} value={c.athlete_id}>
                {c.athlete.full_name}
              </option>
            ))}
          </select>
        )}

        {/* Individual child → the parent-paid plan/subscription, not club
            products (a clubless child has none). */}
        {isIndividualChild(selectedChild) && selectedChild?.subscription && (
          <GameCard className="p-5">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs font-extrabold uppercase tracking-wide text-slate-400">{t("parent.currentPlan", "Abonament")}</p>
                <p className="text-lg font-black text-slate-800 mt-0.5 capitalize truncate">{selectedChild.subscription.plan_code}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selectedChild.subscription.billing_period === "year" ? t("common.year", "anual") : t("common.month", "lunar")}
                </p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                selectedChild.subscription.status === "active" ? "bg-emerald-100 text-emerald-700"
                  : selectedChild.subscription.status === "pending" ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-600"
              }`}>
                {selectedChild.subscription.status === "active" ? t("payment.active", "Activ")
                  : selectedChild.subscription.status === "pending" ? t("payment.pending", "De plată")
                  : selectedChild.subscription.status}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              {t("parent.individualPlanNote", "Pentru un sportiv individual plătești doar abonamentul aplicației. Taxele de club apar aici după ce copilul se alătură unui club.")}
            </p>
          </GameCard>
        )}

        {/* Summary Card */}
        {/* What is actually owed, and the button that settles it.
            Reads the charges — one row per period — rather than the
            subscription rows below, which cannot say "October is unpaid while
            September is paid". Spans every child, because that is how a parent
            thinks about it; the per-child detail stays underneath. */}
        <PayDueCard onPaid={loadPaymentData} />

        {pendingProducts.length > 0 && (
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-white/80 text-sm">{t("payments.totalDue", "Total de plata")}</p>
                <p className="text-2xl font-bold">{formatCurrency(totalPending)}</p>
              </div>
            </div>
            <p className="text-white/70 text-sm">
              {pendingProducts.length} {pendingProducts.length === 1 ? t("payments.service", "serviciu") : t("payments.services", "servicii")}
            </p>
          </div>
        )}

        {/* Payment Methods */}
        {clubPaymentMethods.length > 0 && pendingProducts.length > 0 && (
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <h5 className="text-sm font-medium text-slate-700 mb-2">{t("payment.acceptedMethods", "Metode de plata acceptate")}:</h5>
            <div className="flex flex-wrap gap-2">
              {clubPaymentMethods.includes("cash") && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                  <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {t("payment.cash", "Numerar")}
                </span>
              )}
              {clubPaymentMethods.includes("card") && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  {t("payment.card", "Card")}
                </span>
              )}
              {clubPaymentMethods.includes("bank_transfer") && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                  <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                  {t("payment.bankTransfer", "Transfer bancar")}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setActiveTab("pending")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "pending"
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("payments.pending", "De plata")} ({pendingProducts.length})
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "history"
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("payments.history", "Istoric")} ({paymentHistory.length})
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : activeTab === "pending" ? (
          /* Pending Payments */
          pendingProducts.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
              <svg className="w-16 h-16 text-emerald-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">{t("payments.allPaid", "Toate plățile sunt la zi!")}</h3>
              <p className="text-slate-500 text-sm">{t("payments.noOutstanding", "Nu există plăți restante")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingProducts.map((ap) => {
                const isOverdue = ap.payment_status === "overdue";
                return (
                  <div key={ap.id} className={`bg-white rounded-xl p-4 border ${
                    isOverdue ? "border-red-200" : "border-amber-200"
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-800">{ap.product?.name}</h4>
                        {ap.product?.description && (
                          <p className="text-xs text-slate-500 mt-0.5">{ap.product.description}</p>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isOverdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                      }`}>
                        {isOverdue ? t("payment.overdue", "Întârziat") : t("payment.pending", "De plata")}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-bold text-slate-800">
                          {formatCurrency(ap.product?.price_cents || 0, ap.product?.currency)}
                        </p>
                        <p className="text-xs text-slate-500">
                          / {ap.product?.recurrence_type === "monthly" ? t("common.month", "luna") :
                             ap.product?.recurrence_type === "weekly" ? t("common.week", "săptămâna") :
                             t("common.oneTime", "o singura data")}
                        </p>
                      </div>
                      {ap.next_due_date && (
                        <div className="text-right">
                          <p className="text-xs text-slate-500">{t("payment.dueDate", "Scadenta")}</p>
                          <p className={`text-sm font-medium ${isOverdue ? "text-red-600" : "text-slate-700"}`}>
                            {formatDate(new Date(ap.next_due_date))}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Payment History */
          paymentHistory.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
              <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">{t("payments.noHistory", "Niciun istoric")}</h3>
              <p className="text-slate-500 text-sm">{t("payments.noPaymentsMade", "Nu există plăți efectuate")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentHistory.map((payment) => (
                <div key={payment.id} className="bg-white rounded-xl p-4 border border-slate-100">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-800">
                        {payment.athlete_product?.product?.name || t("payments.unknownProduct", "Serviciu")}
                      </h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {new Date(payment.paid_at).toLocaleDateString("ro-RO", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      {t("payment.paid", "Plătit")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-bold text-emerald-600">
                      {formatCurrency(payment.amount_cents, payment.currency)}
                    </p>
                    <div className="flex items-center gap-2">
                      {payment.payment_method === "cash" && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          {t("payment.cash", "Numerar")}
                        </span>
                      )}
                      {payment.payment_method === "card" && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          {t("payment.card", "Card")}
                        </span>
                      )}
                      {payment.payment_method === "bank_transfer" && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                          </svg>
                          {t("payment.bankTransfer", "Transfer")}
                        </span>
                      )}
                    </div>
                  </div>
                  {payment.notes && (
                    <p className="mt-2 text-xs text-slate-500 italic">{payment.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
      </div>
    </MobileAppLayout>
  );
}
